import {
	ReactElement,
	useCallback
} from "react";
import { InteractionState } from "./TileContext";
import {
	DateString,
	FormattedText,
	Space,
	useAlertDialog,
	useConfirmationDialog
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	AlertManager,
	useAlerts
} from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { capitalizeFirstLetter } from "client/Misc/utils";
import {
	TileConditionalAccess,
	TileData,
	TileMaintenanceWindow
} from "../RenderState/common";

export type UseNavigationResult<TArgs extends readonly unknown[]> = [
	element: ReactElement | null,
	invokeNavigation: (...args: TArgs) => void | Promise<void>
];

export function useBaseNavigation<TArgs extends readonly unknown[]>(
	renderStateIsh: {
		data: TileData;
		interactionState: InteractionState;
	},
	onEdit: (() => void) | undefined,
	handlerOrOtherNavigationResult: ((...args: TArgs) => void | Promise<void>) | UseNavigationResult<TArgs>
): UseNavigationResult<TArgs> {
	const [element, handler] = typeof handlerOrOtherNavigationResult === "function"
		? [null, handlerOrOtherNavigationResult]
		: handlerOrOtherNavigationResult;

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [alertDialog, openAlertDialog] = useAlertDialog();

	const { interactionState, data: tile } = renderStateIsh;

	const invokeNavigation = useCallback(
		async (...args: TArgs): Promise<void> => {
			if (interactionState !== InteractionState.Default) {
				return;
			}

			if (!tile.isConfiguredCorrectly) {
				const trackingId: string = "incomplete-tile-configuration-dialog";
				if (onEdit) {
					const result: TryResult<void> = await openConfirmationDialog({
						title: localize("_IncompleteTileConfiguration"),
						children: localize("_WeAreUnableToOpenThisTileBecauseItIsNotCompletelyConfiguredYet")
							+ " "
							+ localize("_DoYouWantToAddTheMissingInformationNowOrLater"),
						confirmText: localize("_Now"),
						cancelText: localize("_Later"),
						trackingId
					});

					if (result.wasSuccessful) {
						onEdit();
					}
					return;
				} else {
					await openAlertDialog({
						title: localize("_IncompleteTileConfiguration"),
						children: localize("_WeAreUnableToOpenThisTileBecauseItIsNotCompletelyConfiguredYet")
							+ " "
							+ localize("_PleaseContactYourAdmin"),
						confirmText: localize("_Ok"),
						trackingId
					});
				}

				return;
			}

			await handler(...args);
		},
		[
			handler,
			interactionState,
			tile.isConfiguredCorrectly,
			onEdit,
			openAlertDialog,
			openConfirmationDialog
		]
	);

	return [
		confirmationDialog ?? alertDialog ?? element,
		invokeNavigation
	];
}

export function useConditionalAccessNavigation<TArgs extends readonly unknown[]>(
	conditionalAccess: TileConditionalAccess | nil,
	handlerOrOtherNavigationResult: ((...args: TArgs) => void | Promise<void>) | UseNavigationResult<TArgs>
): UseNavigationResult<TArgs> {
	const [element, handler] = typeof handlerOrOtherNavigationResult === "function"
		? [null, handlerOrOtherNavigationResult]
		: handlerOrOtherNavigationResult;

	const alertManager: AlertManager = useAlerts();

	const invokeNavigation = useCallback(
		(...args: TArgs): void | Promise<void> => {
			if (conditionalAccess) {
				alertManager.showInfo(conditionalAccess.reason);
				return;
			}

			return handler(...args);
		},
		[
			alertManager,
			conditionalAccess,
			handler
		]
	);

	return [element, invokeNavigation];
}

export function useMaintenanceWindowNavigation<TArgs extends readonly unknown[]>(
	maintenanceWindow: TileMaintenanceWindow | nil,
	userTimezone: TimeZone,
	handlerOrOtherNavigationResult: ((...args: TArgs) => void) | UseNavigationResult<TArgs>
): UseNavigationResult<TArgs> {
	const [element, handler] = typeof handlerOrOtherNavigationResult === "function"
		? [null, handlerOrOtherNavigationResult]
		: handlerOrOtherNavigationResult;

	const alertManager: AlertManager = useAlerts();

	const invokeNavigation = useCallback(
		(...args: TArgs): void | Promise<void> => {
			if (maintenanceWindow) {
				alertManager.showInfo(
					<>
						<FormattedText
							text={localize("_TheAppIsInMaintenanceUntil")}
							thingsToReplace={[
								{
									what: "{0}",
									filler: (
										<DateString
											value={maintenanceWindow.endsOn}
											format="DMYT"
											timezone={userTimezone}
										/>
									)
								}
							]}
						/>
						{maintenanceWindow.notificationMessage && (
							<>
								<Space />
								{capitalizeFirstLetter(maintenanceWindow.notificationMessage)}.
							</>
						)}
					</>
				);
				return;
			}

			return handler(...args);
		},
		[
			alertManager,
			handler,
			maintenanceWindow,
			userTimezone
		]
	);

	return [element, invokeNavigation];
}
